import { useEffect, useRef, useState } from 'react';
import { call } from './api.js';
import { COUNTRIES, DEFAULT_DIAL } from './countries.js';
import { readCentre } from './place.js';
import { screenLanguage } from './voice.js';

/* ══════════════════════════════════════════════════════════════════════════
 * A MOBILE NUMBER — ONE WAY TO WRITE IT, ONE PICKER, ONE SWITCH
 *
 * Built 8 Oct 2026 with the WhatsApp door. Profile already split a number into
 * its dialling code and the rest (splitPhone / joinPhone, moved here from
 * Profile.jsx unchanged) so joining, signing in and Forgot password by number
 * use the same two halves and save the same "+" and digits.
 *
 * ⚠️ THE WHATSAPP DOOR IS SHOWN ONLY WHEN THE SERVER SAYS IT CAN SEND —
 * GET /auth/channels. Al, 28 Sep 2026: "It shouldn't even be shown until
 * we've got it set up." It switches on by itself the day Al's Render rows
 * name Meta's approved template; nothing here changes that day.
 * ══════════════════════════════════════════════════════════════════════════ */

/* `02_Welcome.html`'s own WhatsApp mark, copied path for path. */
export const WA_PATH = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z';

/* ⚠️ THE LONGEST DIAL CODE WINS. +1 and +1868 (Trinidad) both start the same
   way — matching the first code found would put a Trinidad number under the
   United States. */
export function splitPhone(raw) {
  const s = String(raw || '').replace(/[^\d+]/g, '');
  if (!s.startsWith('+')) return { dial: DEFAULT_DIAL, number: s };
  const digits = s.slice(1);
  let best = '';
  for (const [, , dial] of COUNTRIES) {
    if (digits.startsWith(dial) && dial.length > best.length) best = dial;
  }
  return best
    ? { dial: best, number: digits.slice(best.length) }
    : { dial: DEFAULT_DIAL, number: digits };
}

export function joinPhone(dial, number) {
  const n = String(number || '').replace(/\D/g, '');
  return n ? `+${dial}${n}` : '';
}

/* The country the phone is in (its last known place), else Eleat's default —
   somebody in Calgary is not handed +852 to correct. */
export function deviceDial() {
  const c = readCentre();
  const iso = c && c.country_code ? String(c.country_code).toUpperCase() : null;
  const hit = iso && COUNTRIES.find(([, code]) => code === iso);
  return hit ? hit[2] : DEFAULT_DIAL;
}

/* "+852 •••• 4821" — the locked step 2's own shape: the code, then only the
   last four digits. */
export function maskPhone(phone) {
  const { dial, number } = splitPhone(phone);
  if (!number) return '';
  return `+${dial} •••• ${number.slice(-4)}`;
}

let channels = null;
let asking = null;
export function useChannels() {
  const [c, setC] = useState(channels || { email: true, whatsapp: false });
  useEffect(() => {
    if (channels) return undefined;
    let live = true;
    asking = asking || call('/auth/channels').then((d) => {
      channels = { email: !!(d && d.email), whatsapp: !!(d && d.whatsapp),
        /* Sign in with Google (219) — the public client id, or null = off */
        google_client_id: (d && d.google && d.google_client_id) || null };
      return channels;
    }).catch(() => { asking = null; return null; });
    asking.then((d) => { if (live && d) setC(d); });
    return () => { live = false; };
  }, []);
  return c;
}

/* The number in two halves — `whatsapp_onboarding_new.html` .phonerow: the
   dialling code, then the number. ⚠️ THE LOCKED PICKER, NOT THE PHONE'S LIST —
   Al-Noor, 30 Oct 2026 ("the flag isn't there, and it just looks horrible"):
   `.cc` (flag · +852 · ▼) opens `.cc-menu` — "Search country or code" and one
   row per country (flag · name · +code), the locked file's own classes and
   values. The country list is countries.js, the same list Profile uses. */
function flagOf(iso) {
  return String(iso || '').toUpperCase().replace(/[A-Z]/g, (ch) => String.fromCodePoint(0x1F1E6 + ch.charCodeAt(0) - 65));
}

/* country names in the reader's language — the browser's own list, as the
   Location screen does (Intl.DisplayNames); English where it has none */
const LANG_TAG = { 'zh-HK': 'zh-Hant-HK', 'zh-TW': 'zh-Hant-TW', 'zh-CN': 'zh-Hans-CN', 'fr-CA': 'fr-CA' };
function countryNamer() {
  let lang = 'en';
  try { lang = screenLanguage() || 'en'; } catch { /* English */ }
  if (/^en/i.test(lang)) return null;
  try { const dn = new Intl.DisplayNames([LANG_TAG[lang] || lang], { type: 'region' }); return (iso) => dn.of(iso); }
  catch { return null; }
}

export function PhoneRow({ dial, number, onDial, onNumber, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [find, setFind] = useState('');
  /* +1 is Canada AND the United States — the country is remembered, not just the code */
  const [iso, setIso] = useState(() => {
    const c = readCentre();
    const here = c && c.country_code ? String(c.country_code).toUpperCase() : null;
    const exact = here && COUNTRIES.find(([, code, d]) => code === here && d === String(dial));
    return (exact || COUNTRIES.find(([, , d]) => d === String(dial)) || [])[1] || null;
  });
  const wrap = useRef(null);
  const searchRef = useRef(null);
  const current = COUNTRIES.find(([, code, d]) => code === iso && d === String(dial))
    || COUNTRIES.find(([, , d]) => d === String(dial));

  useEffect(() => {
    if (!open) return undefined;
    const shut = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', shut);
    document.addEventListener('keydown', esc);
    if (searchRef.current) searchRef.current.focus();
    return () => { document.removeEventListener('mousedown', shut); document.removeEventListener('keydown', esc); };
  }, [open]);

  const namer = open ? countryNamer() : null;
  const shown = (name, code) => { try { return (namer && namer(code)) || name; } catch { return name; } };
  const f = find.trim().toLowerCase().replace(/^\+/, '');
  const rows = COUNTRIES.filter(([name, code, d]) => !f || name.toLowerCase().includes(f)
    || shown(name, code).toLowerCase().includes(f) || d.startsWith(f));

  return (
    <div className="phonerow">
      <div className="cc-wrap" ref={wrap}>
        <button type="button" className={'cc' + (open ? ' open' : '')} aria-label="Country dialling code"
          aria-expanded={open} disabled={disabled} onClick={() => { setFind(''); setOpen((v) => !v); }}>
          <span className="flag" aria-hidden="true">{current ? flagOf(current[1]) : ''}</span>
          <span>{`+${dial}`}</span><span className="car" aria-hidden="true">▼</span>
        </button>
        {open && (
          <div className="cc-menu" role="listbox">
            <div className="cc-search">
              <input ref={searchRef} type="text" placeholder="Search country or code" aria-label="Search country or code"
                value={find} onChange={(e) => setFind(e.target.value)} />
            </div>
            <div className="cc-list">
              {rows.map(([name, code, d]) => (
                <button type="button" key={code} className="cc-row" role="option" aria-selected={code === iso}
                  onClick={() => { setIso(code); onDial(d); setOpen(false); }}>
                  <span className="rf" aria-hidden="true">{flagOf(code)}</span>
                  <span className="rn">{shown(name, code)}</span>
                  <span className="rd">{`+${d}`}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <input className="tin" type="tel" inputMode="numeric" autoComplete="tel-national"
        maxLength={15} placeholder="Mobile number" aria-label="Mobile number" readOnly={disabled}
        value={number} onChange={(e) => onNumber(e.target.value.replace(/[^\d ]/g, ''))} />
    </div>
  );
}
